// ── Visual share card (SIGNAL) ───────────────────────────────────
// Renders a 1080x1350 score card as a PNG blob. Shared as an image on
// Android (Filesystem + Share) and via the Web Share API where supported.
// Pure canvas: no emoji, no em-dash, brand gradient white>violet>teal.

export interface ShareCardData {
  outcomeText: string;
  outcomeColor: string;
  nickname: string;
  score: number;
  correct: number;
  total: number;
  avg: string;       // "2.6"
  streak: number;
  realm: string;     // "NEXUS" | "ALL"
  realmColor: string;
  difficulty: string;
}

const W = 1080;
const H = 1350;

export async function renderShareCard(d: ShareCardData): Promise<Blob> {
  // Make sure Inter/JetBrains are usable inside the canvas.
  try { await (document as any).fonts?.ready; } catch { /* ok */ }

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const heading = (px: number) => `italic 900 ${px}px Inter, 'Arial Black', sans-serif`;
  const sans = (px: number, w = 700) => `${w} ${px}px Inter, sans-serif`;
  const monoF = (px: number, w = 500) => `${w} ${px}px 'JetBrains Mono', Consolas, monospace`;

  // Background: the game's sky, never black (the black card predates the
  // night-blue overhaul and read as a different product).
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#1B2B4D'); sky.addColorStop(0.5, '#0F1B33'); sky.addColorStop(1, '#0A1222');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, W - 48, H - 48);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  // Wordmark: WOT with the brand gradient
  ctx.font = heading(170);
  const wotWidth = ctx.measureText('WOT').width;
  const grad = ctx.createLinearGradient(W / 2 - wotWidth / 2, 0, W / 2 + wotWidth / 2, 0);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.45, '#3E7BFA');
  grad.addColorStop(1, '#14B8A6');
  ctx.fillStyle = grad;
  ctx.fillText('WOT', W / 2, 220);

  ctx.font = monoF(30);
  ctx.fillStyle = '#999999';
  drawTracked(ctx, 'WORLD OF TRIVIA', W / 2, 275, 14);

  // Outcome
  ctx.font = heading(120);
  ctx.fillStyle = d.outcomeColor;
  ctx.fillText(d.outcomeText, W / 2, 480);

  // Nickname
  ctx.font = monoF(34);
  ctx.fillStyle = '#777777';
  drawTracked(ctx, d.nickname.toUpperCase(), W / 2, 570, 8);

  // Score
  const scoreStr = d.score.toLocaleString('en-US');
  ctx.font = monoF(150, 700);
  ctx.fillStyle = '#ffffff';
  const scoreW = ctx.measureText(scoreStr).width;
  ctx.fillText(scoreStr, W / 2 - 40, 720);
  ctx.font = monoF(40, 600);
  ctx.fillStyle = '#777777';
  ctx.textAlign = 'left';
  ctx.fillText('PTS', W / 2 - 40 + scoreW / 2 + 24, 720);
  ctx.textAlign = 'center';

  // Stats row: value colored, label gray
  const stats = [
    { v: `${d.correct}/${d.total}`, l: 'CORRECT', c: '#14B8A6' },
    { v: `${d.avg}s`, l: 'AVG', c: '#3E7BFA' },
    { v: `${d.streak}`, l: 'STREAK', c: '#F43F5E' },
  ];
  const cellW = 260;
  const startX = W / 2 - cellW;
  stats.forEach((s, i) => {
    const x = startX + i * cellW;
    ctx.font = monoF(56, 700);
    ctx.fillStyle = s.c;
    ctx.fillText(s.v, x, 850);
    ctx.font = sans(26, 600);
    ctx.fillStyle = '#888888';
    drawTracked(ctx, s.l, x, 895, 4);
  });

  // Realm chip: colored left bar + text
  const chipText = `${d.realm} / ${d.difficulty.toUpperCase()}`;
  ctx.font = monoF(34, 600);
  const chipTextW = ctx.measureText(chipText).width;
  const chipW = chipTextW + 110;
  const chipX = W / 2 - chipW / 2;
  const chipY = 960;
  ctx.fillStyle = '#182541';
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  roundRect(ctx, chipX, chipY, chipW, 76, 12);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = d.realmColor;
  roundRect(ctx, chipX, chipY, 10, 76, 5);
  ctx.fill();
  ctx.fillStyle = '#cccccc';
  ctx.fillText(chipText, W / 2 + 5, chipY + 50);

  // Bottom: accent bar + store hint
  const barW = 320;
  const barGrad = ctx.createLinearGradient(W / 2 - barW / 2, 0, W / 2 + barW / 2, 0);
  barGrad.addColorStop(0, '#14B8A6');
  barGrad.addColorStop(0.5, '#3E7BFA');
  barGrad.addColorStop(1, '#F43F5E');
  ctx.fillStyle = barGrad;
  roundRect(ctx, W / 2 - barW / 2, 1170, barW, 8, 4);
  ctx.fill();

  ctx.font = monoF(30, 700);
  ctx.fillStyle = '#ECEEF6';
  ctx.fillText('wot.aimtech.ai', W / 2, 1245);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

// Letter-spaced centered text (canvas has no reliable letter-spacing).
function drawTracked(ctx: CanvasRenderingContext2D, text: string, cx: number, y: number, tracking: number) {
  const widths = [...text].map(ch => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + tracking * (text.length - 1);
  let x = cx - total / 2;
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  [...text].forEach((ch, i) => {
    ctx.fillText(ch, x, y);
    x += widths[i] + tracking;
  });
  ctx.textAlign = prevAlign;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve((r.result as string).split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

// ═══════════════════════════════════════════════════════════════
// Atlas share card — the rank as the shareable. Wordle's lesson: a
// share is a brag that fits in a glance. Name, number, badges.
// Reads the same lib/atlasRank the app renders from, so the card
// and the screen can never disagree.
// ═══════════════════════════════════════════════════════════════
import type { Atlas, AtlasArchetype } from './cognitiveEngine';
import { fullRegions, nextTierOf, tierOf } from './atlasRank';

export async function renderAtlasShareCard(d: {
  nickname: string;
  atlas: Atlas;
  archetype: AtlasArchetype | null;
}): Promise<Blob> {
  try { await (document as any).fonts?.ready; } catch { /* ok */ }

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const heading = (px: number) => `italic 900 ${px}px Inter, 'Arial Black', sans-serif`;
  const monoF = (px: number, w = 500) => `${w} ${px}px 'JetBrains Mono', Consolas, monospace`;

  // The game's sky, the same as the score and Daily cards.
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#1B2B4D'); sky.addColorStop(0.5, '#0F1B33'); sky.addColorStop(1, '#0A1222');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  ctx.textAlign = 'center';

  // Header
  ctx.font = heading(84);
  const wotW = ctx.measureText('WOT').width;
  const grad = ctx.createLinearGradient(W / 2 - wotW / 2, 0, W / 2 + wotW / 2, 0);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.45, '#3E7BFA');
  grad.addColorStop(1, '#14B8A6');
  ctx.fillStyle = grad;
  ctx.fillText('WOT', W / 2, 130);
  ctx.font = monoF(28);
  ctx.fillStyle = '#999999';
  drawTracked(ctx, 'ATLAS RANK', W / 2, 178, 12);
  ctx.font = monoF(30);
  ctx.fillStyle = '#777777';
  drawTracked(ctx, d.nickname.toUpperCase(), W / 2, 226, 6);

  const cx = W / 2;
  const elo = d.atlas.globalElo;
  const ranked = elo !== null;
  const tier = ranked ? tierOf(elo) : null;
  const next = ranked ? nextTierOf(elo) : null;
  const regions = fullRegions(d.atlas);
  const closest = [...regions].sort((a, b) => b.n - a.n)[0];

  // ── The rank: one name, one number ──
  if (ranked && tier) {
    ctx.font = heading(120);
    ctx.fillStyle = tier.color;
    ctx.fillText(tier.name, cx, 470);
    ctx.font = monoF(64, 700);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(String(elo), cx, 560);
  } else {
    ctx.font = heading(104);
    ctx.fillStyle = '#5a5a5a';
    ctx.fillText('UNRANKED', cx, 470);
    ctx.font = monoF(30);
    ctx.fillStyle = '#999999';
    drawTracked(ctx, `PLACEMENT: ${closest.name.toUpperCase()} ${closest.n} / 10`, cx, 550, 4);
  }

  // Progress bar, same anatomy ranked or not: the empty card leads with the
  // goal, not the void.
  const pbW = 520;
  const pbY = 614;
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  roundRect(ctx, cx - pbW / 2, pbY, pbW, 10, 5);
  ctx.fill();
  const floor = ranked && tier ? (tier.min === -Infinity ? 940 : tier.min) : 0;
  const frac = ranked
    ? next ? Math.max(0.03, Math.min(1, (elo! - floor) / (next.min - floor))) : 1
    : Math.max(0.03, closest.n / 10);
  ctx.fillStyle = ranked && tier ? tier.color : '#3E7BFA';
  roundRect(ctx, cx - pbW / 2, pbY, pbW * frac, 10, 5);
  ctx.fill();
  ctx.font = monoF(26);
  ctx.fillStyle = '#999999';
  drawTracked(
    ctx,
    ranked
      ? next ? `NEXT: ${next.name} · ${next.min - elo!} POINTS` : 'TOP OF THE LADDER'
      : 'CHART A REGION TO GET PLACED',
    cx, pbY + 56, 6,
  );

  // ── The five badges ──
  const by = 830;
  const spacing = 190;
  const hexR = 60;
  regions.forEach((r, i) => {
    const bx = cx + (i - 2) * spacing;
    const locked = r.tier === 'HIDDEN';
    const t = tierOf(r.elo);
    ctx.beginPath();
    for (let k = 0; k < 6; k++) {
      const a = ((k * 60 - 90) * Math.PI) / 180;
      const px = bx + hexR * Math.cos(a);
      const py = by + hexR * Math.sin(a);
      if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fillStyle = locked ? 'rgba(255,255,255,0.04)' : '#182541';
    ctx.fill();
    ctx.strokeStyle = locked ? 'rgba(255,255,255,0.14)' : t.color;
    ctx.lineWidth = locked ? 3 : 5;
    ctx.lineJoin = 'round';
    ctx.stroke();
    // Locked badges are filling dials: a violet arc charges one tenth per
    // answer. (The SVG version charges the hex outline itself; an arc is the
    // canvas-cheap equivalent.)
    if (locked && r.n > 0) {
      ctx.beginPath();
      ctx.arc(bx, by, hexR + 13, -Math.PI / 2, -Math.PI / 2 + (Math.min(10, r.n) / 10) * Math.PI * 2);
      ctx.strokeStyle = '#3E7BFA';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
    ctx.font = heading(46);
    ctx.fillStyle = locked ? '#3a3550' : t.color;
    ctx.fillText(r.name[0], bx, by + 16);
    ctx.font = monoF(19);
    ctx.fillStyle = locked ? '#5a5a5a' : '#b4b4b4';
    drawTracked(ctx, r.name.toUpperCase(), bx, by + hexR + 48, 3);
    ctx.font = monoF(20, 700);
    ctx.fillStyle = locked ? '#5a5a5a' : t.color;
    ctx.fillText(locked ? `${r.n} / 10` : `${t.name} ${Math.round(r.elo)}`, bx, by + hexR + 80);
  });

  ctx.font = monoF(24);
  ctx.fillStyle = '#777777';
  drawTracked(ctx, `${d.atlas.totalAnswers.toLocaleString('en-US')} ANSWERS`, cx, 1004, 8);

  // ── Identity + footer ──
  let y = 1044;
  if (d.archetype) {
    if (d.archetype.leaning) {
      ctx.font = monoF(22);
      ctx.fillStyle = '#777777';
      drawTracked(ctx, 'LEANING', cx, y, 8);
      y += 44;
    }
    ctx.font = heading(64);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(d.archetype.name, cx, y + 20);
    ctx.font = monoF(26);
    ctx.fillStyle = '#999999';
    ctx.fillText(d.archetype.line, cx, y + 70);
    y += 130;
  }

  const barW = 320;
  const barGrad = ctx.createLinearGradient(cx - barW / 2, 0, cx + barW / 2, 0);
  barGrad.addColorStop(0, '#14B8A6');
  barGrad.addColorStop(0.5, '#3E7BFA');
  barGrad.addColorStop(1, '#F43F5E');
  ctx.fillStyle = barGrad;
  roundRect(ctx, cx - barW / 2, 1180, barW, 8, 4);
  ctx.fill();
  ctx.font = monoF(30, 700);
  ctx.fillStyle = '#ECEEF6';
  ctx.fillText('wot.aimtech.ai', cx, 1250);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });
}


// ═══════════════════════════════════════════════════════════════
// Daily share card. Wordle's mechanic, the game's own sky: the number,
// ten dots in the order they were answered, the score, the streak, the
// address. 1080x1080 so it sits square in every feed. The dots say
// "same ten as you" without printing a single question.
// ═══════════════════════════════════════════════════════════════
export interface DailyShareData {
  number: number;          // Daily #N
  dots: boolean[];         // answer outcomes in order, true = correct
  correct: number;
  total: number;
  avg: string;             // "6.4"
  streak: number;          // consecutive days played
}

export async function renderDailyShareCard(d: DailyShareData): Promise<Blob> {
  try { await (document as any).fonts?.ready; } catch { /* ok */ }
  const S = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = S; canvas.height = S;
  const ctx = canvas.getContext('2d')!;
  const heading = (px: number) => `italic 900 ${px}px Inter, 'Arial Black', sans-serif`;
  const sans = (px: number, w = 700) => `${w} ${px}px Inter, sans-serif`;
  const monoF = (px: number, w = 700) => `${w} ${px}px 'JetBrains Mono', Consolas, monospace`;

  // Sky: the app's gradient plus a faint star field, never black.
  const sky = ctx.createLinearGradient(0, 0, 0, S);
  sky.addColorStop(0, '#1B2B4D'); sky.addColorStop(0.5, '#0F1B33'); sky.addColorStop(1, '#0A1222');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, S, S);
  let seed = 1234;
  const rnd = () => { seed = (seed * 1103515245 + 12345) % 2147483648; return seed / 2147483648; };
  for (let i = 0; i < 160; i++) {
    const x = rnd() * S, y = rnd() * S, r = rnd() < 0.15 ? 2.2 : 1.3;
    ctx.fillStyle = `rgba(255,255,255,${0.25 + rnd() * 0.55})`;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';

  // Wordmark
  ctx.font = heading(120);
  const ww = ctx.measureText('WOT').width;
  const grad = ctx.createLinearGradient(S / 2 - ww / 2, 0, S / 2 + ww / 2, 0);
  grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.45, '#60A5FA'); grad.addColorStop(1, '#14B8A6');
  ctx.fillStyle = grad; ctx.fillText('WOT', S / 2, 170);
  ctx.font = monoF(26, 500); ctx.fillStyle = '#8E97B2';
  drawTracked(ctx, 'WORLD OF TRIVIA', S / 2, 212, 12);

  // Title: THE DAILY #N in the card's teal
  ctx.font = heading(84); ctx.fillStyle = '#2DD4BF';
  ctx.fillText(`THE DAILY #${d.number}`, S / 2, 340);

  // Dots: ten, in answer order
  const n = Math.max(1, d.dots.length);
  const gap = 18, dia = Math.min(76, Math.floor((S - 160 - gap * (n - 1)) / n));
  const totalW = n * dia + (n - 1) * gap;
  let x = (S - totalW) / 2 + dia / 2;
  const y = 470;
  for (const ok of d.dots) {
    ctx.beginPath(); ctx.arc(x, y, dia / 2, 0, Math.PI * 2);
    if (ok) { ctx.fillStyle = '#2DD4BF'; ctx.fill(); }
    else { ctx.fillStyle = 'rgba(244,63,94,0.18)'; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = '#F43F5E'; ctx.stroke(); }
    x += dia + gap;
  }

  // Score line
  ctx.font = heading(150); ctx.fillStyle = '#ffffff';
  ctx.fillText(`${d.correct}/${d.total}`, S / 2, 690);
  ctx.font = monoF(34, 500); ctx.fillStyle = '#C0C7DA';
  drawTracked(ctx, `${d.avg}S AVG`, S / 2, 745, 6);

  // The slot under the score never sits empty: a streak pill when there is
  // a streak, otherwise the challenge the card exists to make.
  if (d.streak > 1) {
    const label = `STREAK ${d.streak}`;
    ctx.font = sans(30, 800);
    const w = ctx.measureText(label).width + 70;
    const px = S / 2 - w / 2, py = 800;
    ctx.fillStyle = 'rgba(45,212,191,0.14)'; ctx.strokeStyle = '#2DD4BF'; ctx.lineWidth = 3;
    roundRect(ctx, px, py, w, 62, 31); ctx.fill(); ctx.stroke();
    ctx.fillStyle = '#2DD4BF'; ctx.fillText(label, S / 2, py + 43);
    ctx.font = sans(30, 600); ctx.fillStyle = '#C0C7DA';
    ctx.fillText(`Same ten questions. Can you beat ${d.correct}/${d.total}?`, S / 2, 912);
  } else {
    ctx.font = heading(40); ctx.fillStyle = '#ECEEF6';
    ctx.fillText('Same ten questions.', S / 2, 838);
    ctx.fillStyle = '#2DD4BF';
    ctx.fillText(`Can you beat ${d.correct}/${d.total}?`, S / 2, 892);
  }

  // Address
  ctx.font = monoF(34, 700); ctx.fillStyle = '#ECEEF6';
  ctx.fillText('wot.aimtech.ai', S / 2, S - 96);
  ctx.font = sans(24, 600); ctx.fillStyle = '#8E97B2';
  ctx.fillText('Once a day, for everyone.', S / 2, S - 52);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));
}


// ═══════════════════════════════════════════════════════════════
// Badge share card. One patch, its name and tier, what it took, and the
// rarity when it is a brag. The same sky, wordmark and address as the score
// card, so the four cards read as one game in a feed.
// ═══════════════════════════════════════════════════════════════
export interface BadgeShareData {
  name: string;          // HOT STREAK
  tierLabel: string | null;  // BRONZE · I; null for a single-tier badge
  tierColor: string;     // the metal, for the tier line and the halo
  line: string;          // what it took
  rarity: string | null; // "Only 4% of players have it."
  artUrl: string;        // public/badges/... (same origin, so the canvas stays readable)
  nickname: string;
  date: string;          // 20 OCT 2026
}

export async function renderBadgeShareCard(d: BadgeShareData): Promise<Blob> {
  try { await (document as any).fonts?.ready; } catch { /* ok */ }
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const heading = (px: number) => `italic 900 ${px}px Inter, 'Arial Black', sans-serif`;
  const sans = (px: number, w = 600) => `${w} ${px}px Inter, sans-serif`;
  const monoF = (px: number, w = 600) => `${w} ${px}px 'JetBrains Mono', Consolas, monospace`;

  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#1B2B4D'); sky.addColorStop(0.5, '#0F1B33'); sky.addColorStop(1, '#0A1222');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';

  ctx.font = heading(120);
  const ww = ctx.measureText('WOT').width;
  const grad = ctx.createLinearGradient(W / 2 - ww / 2, 0, W / 2 + ww / 2, 0);
  grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.45, '#3E7BFA'); grad.addColorStop(1, '#14B8A6');
  ctx.fillStyle = grad; ctx.fillText('WOT', W / 2, 168);
  ctx.font = monoF(26, 500); ctx.fillStyle = '#999999';
  drawTracked(ctx, 'WORLD OF TRIVIA', W / 2, 212, 12);

  ctx.font = monoF(28, 700); ctx.fillStyle = '#5EEAD4';
  drawTracked(ctx, 'BADGE EARNED', W / 2, 300, 10);

  // The patch on a faint halo of its own metal.
  const cy = 560, size = 440;
  const halo = ctx.createRadialGradient(W / 2, cy, 40, W / 2, cy, 330);
  halo.addColorStop(0, `${d.tierColor}55`); halo.addColorStop(1, `${d.tierColor}00`);
  ctx.fillStyle = halo; ctx.fillRect(W / 2 - 340, cy - 340, 680, 680);
  try {
    const img = new Image();
    img.src = d.artUrl;
    await img.decode();
    ctx.drawImage(img, W / 2 - size / 2, cy - size / 2, size, size);
  } catch { /* no art is better than no card */ }

  // The name fits the card at any length (TRAVEL COMPANION is the longest).
  let px = 96;
  ctx.font = heading(px);
  while (ctx.measureText(d.name).width > W - 160 && px > 60) { px -= 4; ctx.font = heading(px); }
  ctx.fillStyle = '#ffffff'; ctx.fillText(d.name, W / 2, 878);

  let y = 878;
  if (d.tierLabel) {
    y += 64;
    ctx.font = monoF(32, 700); ctx.fillStyle = d.tierColor;
    drawTracked(ctx, d.tierLabel, W / 2, y, 8);
  }
  y += 66;
  ctx.font = sans(38); ctx.fillStyle = '#D5D9E6';
  ctx.fillText(d.line, W / 2, y);
  if (d.rarity) {
    y += 56;
    ctx.font = sans(32, 700); ctx.fillStyle = '#2DD4BF';
    ctx.fillText(d.rarity, W / 2, y);
  }

  ctx.font = monoF(26, 500); ctx.fillStyle = '#8A90A2';
  drawTracked(ctx, `${d.nickname.toUpperCase()} · ${d.date}`, W / 2, 1140, 6);

  const barW = 320;
  const barGrad = ctx.createLinearGradient(W / 2 - barW / 2, 0, W / 2 + barW / 2, 0);
  barGrad.addColorStop(0, '#14B8A6'); barGrad.addColorStop(0.5, '#3E7BFA'); barGrad.addColorStop(1, '#F43F5E');
  ctx.fillStyle = barGrad;
  roundRect(ctx, W / 2 - barW / 2, 1180, barW, 8, 4);
  ctx.fill();
  ctx.font = monoF(30, 700); ctx.fillStyle = '#ECEEF6';
  ctx.fillText('wot.aimtech.ai', W / 2, 1250);

  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'));
}

/** The text twin of the card, for chats that strip images. Dots, not emoji. */
export function dailyShareText(d: DailyShareData, url: string): string {
  const dots = d.dots.map((ok) => (ok ? '\u25CF' : '\u25CB')).join(' ');
  const lines = [`World of Trivia Daily #${d.number}`, `${dots}  ${d.correct}/${d.total} in ${d.avg}s avg`];
  if (d.streak > 1) lines.push(`Streak ${d.streak}`);
  lines.push(url);
  return lines.join('\n');
}
